import { Controller } from "@hotwired/stimulus"
import { each } from "lodash"

declare const grecaptcha: any

declare global {
  interface Window {
    grecaptcha: any
  }
}

export default class FormController extends Controller {
  static targets = [
    "recaptchaResponseField",
    "submitButton",
  ]

  static values = {
    recaptchaEnabled: Boolean,
    recaptchaAction: String,
    recaptchaSiteKey: String,
  }

  declare readonly recaptchaEnabledValue: boolean
  declare readonly recaptchaActionValue: string
  declare readonly recaptchaSiteKeyValue: string
  declare readonly recaptchaResponseFieldTarget: HTMLInputElement
  declare readonly submitButtonTarget: HTMLButtonElement

  declare readonly hasRecaptchaEnabledValue: boolean
  declare readonly hasRecaptchaActionValue: boolean
  declare readonly hasRecaptchaSiteKeyValue: boolean
  declare readonly hasRecaptchaResponseFieldTarget: boolean
  declare readonly hasSubmitButtonTarget: boolean

  private requiredFields: NodeListOf<HTMLInputElement> | undefined = undefined
  private requiredSelects: NodeListOf<HTMLSelectElement> | undefined = undefined

  connect() {
    // console.debug("FormController#connect")
    if (!this.recaptchaEnabledValue) {
      console.warn("recaptcha verification disabled, setting window.grecaptcha to a mock for testing of event handler logic")
      window.grecaptcha = {
        enterprise: {
          ready: (callback: () => void) => {
            callback()
          },

          execute: (siteKey: string, options: unknown) => {
            return new Promise((resolve, reject) => {
              resolve("recaptcha-token")
            })
          },
        },
      }
    }

    if (!this.hasRecaptchaActionValue) {
      throw new Error("Missing required recaptcha action value")
    }
    if (!this.hasRecaptchaSiteKeyValue) {
      throw new Error("Missing required recaptchaSiteKey value")
    }
    if (!this.hasRecaptchaResponseFieldTarget) {
      throw new Error("Missing required recaptchaResponseField target")
    }
    if (!this.hasSubmitButtonTarget) {
      throw new Error("Missing required submitButton target")
    }

    this.requiredFields = this.element.querySelectorAll("input.required")
    this.requiredSelects = this.element.querySelectorAll("select.required")

    this.validate()
  }

  validate() {
    let valid = true

    if (this.requiredFields) {
      // use of lodash provides better backwards compatibility with older browsers that don't allow iteration over NodeList
      each(this.requiredFields, (input) => {
        if (input.value.trim() === "") {
          valid = false
          return false;
        }
      })
    }

    if (this.requiredSelects) {
      // use of lodash provides better backwards compatibility with older browsers that don't allow iteration over NodeList
      each(this.requiredSelects, (select) => {
        if (select.value.trim() === "") {
          valid = false
          return false;
        }
      })
    }

    this.submitButtonTarget.disabled = !valid
  }

  verifyRecaptcha(event: Event) {
    if (!event) {
      throw new Error("Missing required event")
    }

    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.innerText = "Please wait..."

    if (typeof grecaptcha === "undefined") {
      console.warn("grecaptcha not loaded, skipping verification")
      return
    }

    if (this.verified) {
      // console.debug("recaptcha already verified, submitting form normally")
      return
    }

    event.preventDefault()

    grecaptcha.enterprise.ready(async () => {
      const token = await grecaptcha.enterprise.execute(
        this.recaptchaSiteKeyValue,
        {
          action: this.recaptchaActionValue,
        })

      // console.debug("got recaptcha token, submitting form")
      this.recaptchaResponseFieldTarget.value = token
      this.submitButtonTarget.disabled = false

      // Avoids re-submitting the form recursively (in the same handler)
      setTimeout(() => {
        // console.debug("re-submitting form")
        this.submitButtonTarget.click()
      }, 10)
      // console.debug("end of submit handler")
    })
  }

  get verified() {
    return this.recaptchaResponseFieldTarget.value.trim() !== ""
  }
}
